import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createOrgUnitType,
    fetchOrgUnitTypes,
    fieldDetailMessage,
    OrgAdminApiError,
    updateOrganization,
    updateOrgUnitType,
    type OrgUnitType,
} from '@/api/orgAdmin';
import type { ToastApi } from '@/components/ui';
import { translateParentRuleMessage } from './dragRules';
import { slugify } from './orgText';
import type { OrganizationTreeNode } from './types';

/**
 * EAA-1838: the root organization's unit-type vocabulary — one hook for every
 * unit-type surface, because they all share the same list and the same cache key:
 *
 *   1. The REQUIRED picker in "Thêm đơn vị con" (`OrgCreateModal`), including the
 *      inline quick-create below.
 *   2. "Đổi loại đơn vị" on a row — a `PATCH /organizations/{id}` with
 *      `{unit_type}`; nothing to do with authoring the vocabulary.
 *
 * REPLACED `useOrgTiers`. The differences that matter to callers:
 *
 *   - No `rank`. Ordering a set of types linearly was the modelling error; the
 *     shape lives in each type's `allowed_children` and is read as a graph.
 *   - No `allow_self_nest`. A type that may nest under itself lists its own id.
 *   - `createInline` is new, and it is the whole point of this pass. Previously
 *     the ONLY way to author a rung was a separate page-level panel reached from
 *     a toolbar button labelled like a filter — so an admin who discovered mid-
 *     create that the type they wanted did not exist had to abandon the modal,
 *     find the panel, scroll past three paragraphs to a footer button, then come
 *     back. Now the picker itself creates the type and returns it.
 *
 * ONE FETCH, PER ROOT ORG. `GET /organizations/{rootOrgId}/unit-types` is
 * org-scoped and the whole vocabulary is a handful of rows, so it is fetched once
 * for the screen and shared by the row badges, the create modal, the retype
 * dialog and `dragRules.checkUnitTypeContainment`. Deliberately fetched WITHOUT
 * `is_active` filtering: the server's default returns hidden rows too, and a
 * đơn vị may still reference a hidden type — a picker that dropped it would
 * silently render the org as something it is NOT. Callers wanting an
 * active-only list filter `activeUnitTypes` below.
 *
 * TWO PERMISSION GATES, and they are NOT the same one — verified against
 * portal-api rather than assumed:
 *   - Authoring the vocabulary (POST/PATCH `.../unit-types`) is SUPER-ADMIN ONLY,
 *     checked inside `org_unit_type_views.py` after the coarse `IsPortalAdmin`.
 *     That maps to this screen's `canManageTree` (= `console.orgs.manage`).
 *   - Assigning a type to an org is NOT super-admin-only: the gate in
 *     `OrganizationDetailView.patch` covers `is_active` and `parent` only, so an
 *     org-admin may set `unit_type` on an org they administer. That maps to
 *     `canManage`, exactly like rename.
 * Reading is allowed for both roles, so an org-admin gets a plain picker while a
 * super-admin gets the same picker plus its create branch.
 */

export interface UseOrgUnitTypesArgs {
    apiBase: string;
    accessToken: string | null;
    /** The acting org — the vocabulary's owner (see `useOrgTree`'s root decision). */
    rootOrgId: string | null;
    enabled: boolean;
    toast: ToastApi;
    /** From `useOrgTree` — a retype changes what the org's row renders. */
    invalidateParents: (parentIds: Array<string | null>) => void;
    invalidateOrg: (orgId: string) => void;
}

// EAA-1845: takes `t` explicitly rather than calling `useTranslation()` itself —
// this is a plain helper (not a hook), called from inside a `useMutation` error
// callback, so it cannot call a hook of its own.
function unitTypeErrMessage(t: TFunction, error: unknown, fallback: string): string {
    if (!(error instanceof OrgAdminApiError)) return fallback;
    // Field errors from OrgUnitTypeCreateSerializer arrive under their own field
    // keys (`code`, `name`); the duplicate-code case is by far the likeliest, and
    // its server text is English, so it is surfaced explicitly.
    const codeDetail = fieldDetailMessage(error.details, 'code');
    if (codeDetail?.startsWith('A unit type with this code already exists')) {
        return t('console.hierarchy.hooks.unitTypes.duplicateCodeError');
    }
    return error.message || fallback;
}

export function useOrgUnitTypes({
    apiBase,
    accessToken,
    rootOrgId,
    enabled,
    toast,
    invalidateParents,
    invalidateOrg,
}: UseOrgUnitTypesArgs) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const unitTypesQuery = useQuery({
        queryKey: ['org-unit-types', accessToken, rootOrgId],
        queryFn: () => fetchOrgUnitTypes(apiBase, accessToken as string, rootOrgId as string),
        enabled: enabled && Boolean(rootOrgId),
        retry: false,
    });

    // Memoized because these feed dependency arrays (and the containment check
    // through `useHierarchyDrag`); a bare `data ?? []` would mint a new array per
    // render.
    const unitTypes = useMemo(() => unitTypesQuery.data ?? [], [unitTypesQuery.data]);
    const activeUnitTypes = useMemo(() => unitTypes.filter((t) => t.is_active), [unitTypes]);
    const unitTypesById = useMemo(
        () => new Map<string, OrgUnitType>(unitTypes.map((t) => [t.id, t])),
        [unitTypes]
    );

    const invalidateUnitTypes = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: ['org-unit-types', accessToken, rootOrgId],
            }),
        [queryClient, accessToken, rootOrgId]
    );

    /* --------------------------------------------- inline create from a picker */

    const createUnitType = useMutation({
        mutationFn: (body: {
            name: string;
            code: string;
            allowed_children?: string[];
            can_be_root?: boolean;
        }) => createOrgUnitType(apiBase, accessToken as string, rootOrgId as string, body),
        onSuccess: async (unitType) => {
            toast.success(
                t('console.hierarchy.hooks.unitTypes.createSuccessToast', {
                    name: unitType.name,
                })
            );
            await invalidateUnitTypes();
        },
    });

    /**
     * Create a type from inside a picker and hand it straight back, so the caller
     * can select it without a second round trip through the list.
     *
     * `parentTypeId` is the reason this belongs here rather than in a generic
     * settings form: a type created while adding a child đơn vị is, by
     * construction, meant to sit under that parent's type — so the containment
     * edge is added to the PARENT in the same breath. Skipping that would create a
     * type the server then refuses to place, which is exactly the kind of
     * dead-end the old separate panel produced.
     *
     * Returns `null` on failure; the caller surfaces the message.
     */
    const createInline = useCallback(
        async (name: string, parentTypeId: string | null): Promise<OrgUnitType | null> => {
            const trimmed = name.trim();
            if (!trimmed) return null;
            try {
                const created = await createUnitType.mutateAsync({
                    name: trimmed,
                    code: slugify(trimmed),
                    // No parent type means this is being created for a root-level
                    // đơn vị, so it must be allowed to sit at depth 0.
                    can_be_root: !parentTypeId,
                });
                if (parentTypeId) {
                    const parentType = unitTypesById.get(parentTypeId);
                    const nextChildren = [
                        ...(parentType?.allowed_children ?? []),
                        created.id,
                    ];
                    await updateOrgUnitType(
                        apiBase,
                        accessToken as string,
                        rootOrgId as string,
                        parentTypeId,
                        { allowed_children: nextChildren }
                    );
                    await invalidateUnitTypes();
                }
                return created;
            } catch {
                return null;
            }
        },
        [createUnitType, unitTypesById, apiBase, accessToken, rootOrgId, invalidateUnitTypes]
    );

    /* ----------------------------------------------- retype one đơn vị (a row) */

    const [retypeNodeId, setRetypeNodeId] = useState<string | null>(null);
    const [retypeChoice, setRetypeChoice] = useState<string>('');
    const [retypeError, setRetypeError] = useState<string | null>(null);

    const openRetype = useCallback((node: OrganizationTreeNode) => {
        setRetypeNodeId(node.id);
        setRetypeChoice(node.organization.unit_type ?? '');
        setRetypeError(null);
    }, []);
    const closeRetype = useCallback(() => {
        setRetypeNodeId(null);
        setRetypeError(null);
    }, []);

    const retypeOrg = useMutation({
        mutationFn: (vars: { orgId: string; unitTypeId: string }) =>
            updateOrganization(apiBase, accessToken as string, vars.orgId, {
                unit_type: vars.unitTypeId,
            }),
        onSuccess: (organization) => {
            setRetypeNodeId(null);
            setRetypeError(null);
            const typeName = organization.unit_type
                ? unitTypesById.get(organization.unit_type)?.name
                : null;
            toast.success(
                typeName
                    ? t('console.hierarchy.hooks.unitTypes.retypeSuccessNamed', {
                          orgName: organization.name,
                          typeName,
                      })
                    : t('console.hierarchy.hooks.unitTypes.retypeSuccessGeneric', {
                          orgName: organization.name,
                      })
            );
            // The row renders the badge from its own `Organization` row, so both
            // the org query and its parent's children list have to be refreshed —
            // the tree reads the row from whichever of the two loaded it.
            invalidateOrg(organization.id);
            invalidateParents([organization.parent]);
        },
        onError: (error) => {
            // EAA-1838: a containment rejection comes back under `details.parent`
            // like every other ValueError from this view, so it goes through the
            // same translator the move path uses rather than leaking English.
            const translated =
                error instanceof OrgAdminApiError
                    ? translateParentRuleMessage(fieldDetailMessage(error.details, 'parent'))
                    : null;
            setRetypeError(
                translated ??
                    unitTypeErrMessage(
                        t,
                        error,
                        t('console.hierarchy.hooks.unitTypes.retypeErrorFallback')
                    )
            );
        },
    });

    const confirmRetype = useCallback(() => {
        if (!retypeNodeId || !retypeChoice) return;
        retypeOrg.mutate({ orgId: retypeNodeId, unitTypeId: retypeChoice });
    }, [retypeNodeId, retypeChoice, retypeOrg]);

    /**
     * Every member is `unitType*`/`retype*`-prefixed on the way out, deliberately:
     * `useHierarchyPage` spreads this object into its own return value alongside
     * the org-rename and assignment-form state, which already own the bare names
     * `renameDraft`, `setRenameDraft` and `assignError`. Prefixing here is what
     * stops that spread from silently shadowing them.
     */
    return {
        unitTypesQuery,
        unitTypes,
        activeUnitTypes,
        unitTypesById,
        createUnitType,
        createInlineUnitType: createInline,
        // retype one đơn vị
        retypeNodeId,
        retypeChoice,
        setRetypeChoice,
        retypeError,
        openRetype,
        closeRetype,
        confirmRetype,
        retypeOrg,
    };
}

export type OrgUnitTypesVM = ReturnType<typeof useOrgUnitTypes>;
